import React, { useCallback, useEffect, useRef, useState } from 'react'

import { DEFAULT_SETTINGS } from '../../core/defaults'
import { DEFAULT_EFFECTS_STATE } from '../../core/effects/types'
import type { EffectsState } from '../../core/effects/types'
import { ExportManager } from '../../core/export/ExportManager'
import { prepareDownloadPayload } from '../../core/export/download-payload'
import { getRuntimeContent } from '../../core/export/runtime-content'
import {
  DEFAULT_GIF_EXPORT_CONFIG,
  DEFAULT_HTMLJS_EXPORT_CONFIG,
  DEFAULT_IMAGE_EXPORT_CONFIG,
  DEFAULT_REACT_EXPORT_CONFIG,
  downloadBlob,
  downloadText,
  formatFileSize,
} from '../../core/export/types'
import type {
  ExportFormat,
  ExportProgress,
  ExportResult,
  GifExportConfig,
  HtmlJsExportConfig,
  ImageExportConfig,
  ReactExportConfig,
} from '../../core/export/types'
import { createZipBlob, createZipFilename } from '../../core/export/ZipExporter'
import type { FullRenderSettings } from '../../core/types'
import CollapsibleSection from './CollapsibleSection'

const STATUS_LABELS: Record<string, string> = {
  idle: '等待中',
  preparing: '准备中',
  capturing: '采集中',
  rendering_frames: '逐帧渲染中',
  encoding: '编码中',
  muxing: '封装中',
  cancelled: '已取消',
  completed: '已完成',
  error: '错误',
}

const GIF_RESOLUTION_OPTIONS: GifExportConfig['resolutionPreset'][] = ['source', '240p', '360p', '480p', '720p']

const FormatSelector: React.FC<{
  current: ExportFormat
  onChange: (format: ExportFormat) => void
}> = ({ current, onChange }) => {
  const formats: { value: ExportFormat; label: string }[] = [
    { value: 'image', label: '图片' },
    { value: 'gif', label: 'GIF' },
    { value: 'html-js', label: 'HTML+JS' },
    { value: 'react', label: 'React' },
  ]

  return (
    <div className="xp-toolbar">
      {formats.map((fmt) => (
        <button
          key={fmt.value}
          onClick={() => onChange(fmt.value)}
          className={`xp-btn xp-btn-small ${current === fmt.value ? 'xp-btn-pressed' : ''}`}
        >
          {fmt.label}
        </button>
      ))}
    </div>
  )
}

const ProgressBar: React.FC<{ progress: ExportProgress }> = ({ progress }) => (
  <div className="mt-3 space-y-1">
    <div className="flex justify-between text-[15px] font-semibold">
      <span>{STATUS_LABELS[progress.status] || progress.status}</span>
      <span>{progress.progress}%</span>
    </div>
    <div className="xp-progress">
      <div
        className={progress.status === 'error' ? 'xp-progress-bar xp-progress-bar-error' : 'xp-progress-bar'}
        style={{ width: `${progress.progress}%` }}
      />
    </div>
    {progress.message && <p className="xp-muted text-[15px] font-semibold">{progress.message}</p>}
    {progress.currentFrame !== undefined && progress.totalFrames !== undefined && (
      <p className="xp-muted text-[15px] font-semibold">
        帧 {progress.currentFrame} / {progress.totalFrames}
      </p>
    )}
  </div>
)

const ExportResultView: React.FC<{
  result: ExportResult
  onDownload: () => void
  onClear: () => void
}> = ({ result, onDownload, onClear }) => {
  if (!result.success) {
    return <div className="xp-error mt-3">导出失败: {result.error}</div>
  }

  return (
    <div className="xp-success mt-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-bold text-[16px]">{result.filename}</div>
          <div className="text-[15px] font-semibold">文件大小: {formatFileSize(result.size)}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={onDownload} className="xp-btn xp-btn-small">
            下载
          </button>
          <button onClick={onClear} className="xp-btn xp-btn-small">
            清除
          </button>
        </div>
      </div>
      {result.externalJsFilename && (
        <div className="xp-info">
          分离模式会自动打包为 ZIP，包含 {result.filename} 和 {result.externalJsFilename}。
        </div>
      )}
    </div>
  )
}

const ImageConfigPanel: React.FC<{
  config: ImageExportConfig
  onChange: (config: Partial<ImageExportConfig>) => void
}> = ({ config, onChange }) => (
  <div className="space-y-2 mt-3">
    <div>
      <label className="xp-group-label">格式</label>
      <select value={config.format} onChange={(e) => onChange({ format: e.target.value as ImageExportConfig['format'] })} className="xp-select">
        <option value="png">PNG (无损)</option>
        <option value="jpg">JPG (有损)</option>
        <option value="webp">WebP</option>
      </select>
    </div>
    {(config.format === 'jpg' || config.format === 'webp') && (
      <div>
        <label className="xp-group-label">质量: {config.quality}%</label>
        <input
          type="range"
          min="10"
          max="100"
          value={config.quality}
          onChange={(e) => onChange({ quality: parseInt(e.target.value, 10) })}
          className="w-full"
        />
      </div>
    )}
    <div>
      <label className="xp-group-label">缩放倍数</label>
      <select value={config.scale} onChange={(e) => onChange({ scale: parseInt(e.target.value, 10) })} className="xp-select">
        <option value="1">1x</option>
        <option value="2">2x</option>
        <option value="4">4x</option>
      </select>
    </div>
    {config.format === 'jpg' && (
      <div>
        <label className="xp-group-label">背景色</label>
        <input
          type="color"
          value={config.backgroundColor || '#000000'}
          onChange={(e) => onChange({ backgroundColor: e.target.value })}
          className="w-full"
        />
      </div>
    )}
  </div>
)

const GifConfigPanel: React.FC<{
  config: GifExportConfig
  onChange: (config: Partial<GifExportConfig>) => void
}> = ({ config, onChange }) => (
  <div className="space-y-2 mt-3">
    <div className="xp-info">
      GIF 使用离线逐帧渲染，不依赖预览 canvas 的实时刷新；适合短动画和可分享预览。
    </div>
    <div>
      <label className="xp-group-label">分辨率</label>
      <select
        value={config.resolutionPreset}
        onChange={(e) => onChange({ resolutionPreset: e.target.value as GifExportConfig['resolutionPreset'] })}
        className="xp-select"
      >
        {GIF_RESOLUTION_OPTIONS.map((preset) => (
          <option key={preset} value={preset}>
            {preset === 'source' ? '跟随源尺寸' : preset}
          </option>
        ))}
      </select>
    </div>
    <div>
      <label className="xp-group-label">帧率 (FPS)</label>
      <select value={config.fps} onChange={(e) => onChange({ fps: parseInt(e.target.value, 10) })} className="xp-select">
        <option value="10">10 FPS</option>
        <option value="15">15 FPS</option>
        <option value="24">24 FPS</option>
      </select>
    </div>
    <div>
      <label className="xp-group-label">时长: {config.duration} 秒</label>
      <input
        type="range"
        min="1"
        max="5"
        value={config.duration}
        onChange={(e) => onChange({ duration: parseInt(e.target.value, 10) })}
        className="w-full"
      />
    </div>
    <div>
      <label className="xp-group-label">编码质量: {config.quality}</label>
      <input
        type="range"
        min="1"
        max="20"
        value={config.quality}
        onChange={(e) => onChange({ quality: parseInt(e.target.value, 10) })}
        className="w-full"
      />
    </div>
    <div>
      <label className="xp-group-label">循环次数</label>
      <select value={config.loop} onChange={(e) => onChange({ loop: parseInt(e.target.value, 10) })} className="xp-select">
        <option value="0">无限循环</option>
        <option value="1">1 次</option>
        <option value="3">3 次</option>
      </select>
    </div>
    <div className="xp-note">
      GIF 天然只有有限色彩，建议使用 720p 以内、5 秒以内的短动画。
    </div>
  </div>
)

const HtmlJsConfigPanel: React.FC<{
  config: HtmlJsExportConfig
  onChange: (config: Partial<HtmlJsExportConfig>) => void
}> = ({ config, onChange }) => (
  <div className="space-y-2 mt-3">
    <label className="xp-checkbox-row">
      <span className="xp-field-label">透明背景</span>
      <input type="checkbox" checked={config.transparent} onChange={(e) => onChange({ transparent: e.target.checked })} />
    </label>
    <label className="xp-checkbox-row">
      <span className="xp-field-label">离屏暂停</span>
      <input type="checkbox" checked={config.pauseOffScreen} onChange={(e) => onChange({ pauseOffScreen: e.target.checked })} />
    </label>
    <label className="xp-checkbox-row">
      <span className="xp-field-label">鼠标交互</span>
      <input type="checkbox" checked={config.interaction} onChange={(e) => onChange({ interaction: e.target.checked })} />
    </label>
    <div>
      <label className="xp-group-label">输出方式</label>
      <select value={config.outputMode} onChange={(e) => onChange({ outputMode: e.target.value as HtmlJsExportConfig['outputMode'] })} className="xp-select">
        <option value="inline">内联</option>
        <option value="split">分离 JS</option>
      </select>
    </div>
  </div>
)

const ReactConfigPanel: React.FC<{
  config: ReactExportConfig
  onChange: (config: Partial<ReactExportConfig>) => void
}> = ({ config, onChange }) => (
  <div className="space-y-2 mt-3">
    <div>
      <label className="xp-group-label">组件名称</label>
      <input type="text" value={config.componentName} onChange={(e) => onChange({ componentName: e.target.value })} className="xp-input" />
    </div>
    <label className="xp-checkbox-row">
      <span className="xp-field-label">包含类型定义</span>
      <input type="checkbox" checked={config.includeTypes} onChange={(e) => onChange({ includeTypes: e.target.checked })} />
    </label>
    <label className="xp-checkbox-row">
      <span className="xp-field-label">分离运行时 JS</span>
      <input type="checkbox" checked={config.splitRuntime} onChange={(e) => onChange({ splitRuntime: e.target.checked })} />
    </label>
    <div className="xp-note">分离模式适合在多个项目中复用导出结果。</div>
  </div>
)

interface ExportPanelProps {
  canvas?: HTMLCanvasElement | null
  sourceImage?: HTMLImageElement | null
  settings?: FullRenderSettings
  effectsState?: EffectsState
}

const ExportPanel: React.FC<ExportPanelProps> = ({ canvas, sourceImage, settings, effectsState }) => {
  const [format, setFormat] = useState<ExportFormat>('image')
  const [isExporting, setIsExporting] = useState(false)
  const [progress, setProgress] = useState<ExportProgress>({ status: 'idle', progress: 0 })
  const [result, setResult] = useState<ExportResult | null>(null)

  const [imageConfig, setImageConfig] = useState<ImageExportConfig>(DEFAULT_IMAGE_EXPORT_CONFIG)
  const [gifConfig, setGifConfig] = useState<GifExportConfig>(DEFAULT_GIF_EXPORT_CONFIG)
  const [htmlJsConfig, setHtmlJsConfig] = useState<HtmlJsExportConfig>(DEFAULT_HTMLJS_EXPORT_CONFIG)
  const [reactConfig, setReactConfig] = useState<ReactExportConfig>(DEFAULT_REACT_EXPORT_CONFIG)

  const exportManagerRef = useRef<ExportManager | null>(null)

  useEffect(() => {
    exportManagerRef.current = new ExportManager()
    exportManagerRef.current.onProgress(setProgress)
    exportManagerRef.current.onComplete((nextResult) => {
      setResult(nextResult)
      setIsExporting(false)
    })

    return () => {
      exportManagerRef.current?.destroy()
    }
  }, [])

  const getActiveCanvas = useCallback((): HTMLCanvasElement | null => {
    if (canvas && canvas.width > 0 && canvas.height > 0) {
      return canvas
    }

    const mainCanvas = document.querySelector('main canvas') as HTMLCanvasElement | null
    if (mainCanvas && mainCanvas.width > 0 && mainCanvas.height > 0) {
      return mainCanvas
    }

    return null
  }, [canvas])

  const handleExport = useCallback(async () => {
    const activeCanvas = getActiveCanvas()
    if (!activeCanvas) {
      setResult({ success: false, filename: '', size: 0, error: '请先生成预览画面', timestamp: Date.now() })
      return
    }

    setIsExporting(true)
    setResult(null)
    setProgress({ status: 'preparing', progress: 0 })

    try {
      switch (format) {
        case 'image': {
          const imageResult = await exportManagerRef.current!.exportImage(activeCanvas, imageConfig)
          setResult(imageResult)
          setIsExporting(false)
          return
        }

        case 'gif': {
          if (!sourceImage) {
            setResult({ success: false, filename: '', size: 0, error: '请先上传图片', timestamp: Date.now() })
            setIsExporting(false)
            return
          }

          const gifResult = await exportManagerRef.current!.exportGif(
            sourceImage,
            settings || DEFAULT_SETTINGS,
            effectsState || DEFAULT_EFFECTS_STATE,
            gifConfig
          )
          setResult(gifResult)
          setIsExporting(false)
          return
        }

        case 'html-js': {
          if (!sourceImage) {
            setResult({ success: false, filename: '', size: 0, error: '请先上传图片并生成预览', timestamp: Date.now() })
            setIsExporting(false)
            return
          }

          setProgress({ status: 'preparing', progress: 30, message: '正在生成 HTML+JS 代码...' })
          const htmlResult = await exportManagerRef.current!.exportHtmlJs(
            activeCanvas,
            sourceImage,
            settings || DEFAULT_SETTINGS,
            effectsState || DEFAULT_EFFECTS_STATE,
            htmlJsConfig
          )
          setResult(htmlResult)
          setProgress(htmlResult.success
            ? { status: 'completed', progress: 100, message: '导出完成' }
            : { status: 'error', progress: 100, message: htmlResult.error || 'HTML+JS 导出失败' })
          setIsExporting(false)
          return
        }

        case 'react': {
          if (!sourceImage) {
            setResult({ success: false, filename: '', size: 0, error: '请先上传图片', timestamp: Date.now() })
            setIsExporting(false)
            return
          }

          setProgress({ status: 'preparing', progress: 30, message: '正在生成 React 组件代码...' })
          const reactResult = await exportManagerRef.current!.exportReact(
            sourceImage,
            settings || DEFAULT_SETTINGS,
            effectsState || DEFAULT_EFFECTS_STATE,
            reactConfig
          )
          setResult(reactResult)
          setProgress(reactResult.success
            ? { status: 'completed', progress: 100, message: 'React 组件导出完成' }
            : { status: 'error', progress: 100, message: reactResult.error || 'React 导出失败' })
          setIsExporting(false)
          return
        }
      }
    } catch (error) {
      setResult({
        success: false,
        filename: '',
        size: 0,
        error: error instanceof Error ? error.message : '导出失败',
        timestamp: Date.now(),
      })
      setIsExporting(false)
    }
  }, [effectsState, format, getActiveCanvas, gifConfig, htmlJsConfig, imageConfig, reactConfig, settings, sourceImage])

  const handleCancel = useCallback(() => {
    exportManagerRef.current?.cancelGifExport()
    setIsExporting(false)
    setProgress({ status: 'idle', progress: 0 })
  }, [])

  const handleDownload = useCallback(async () => {
    if (!result) {
      return
    }

    const payload = await prepareDownloadPayload(result, getRuntimeContent)
    if (payload.kind === 'zip') {
      downloadBlob(createZipBlob(payload.entries), createZipFilename(payload.filename))
      return
    }
    if (payload.kind === 'blob') {
      downloadBlob(payload.blob, payload.filename)
      return
    }
    if (payload.kind === 'text') {
      downloadText(payload.content, payload.filename)
    }
  }, [result])

  const handleClear = useCallback(() => {
    setResult(null)
    setProgress({ status: 'idle', progress: 0 })
  }, [])

  return (
    <CollapsibleSection title="导出" icon="EXP" defaultExpanded={false}>
      <FormatSelector current={format} onChange={setFormat} />

      {format === 'image' && <ImageConfigPanel config={imageConfig} onChange={(nextConfig) => setImageConfig({ ...imageConfig, ...nextConfig })} />}
      {format === 'gif' && <GifConfigPanel config={gifConfig} onChange={(nextConfig) => setGifConfig({ ...gifConfig, ...nextConfig })} />}
      {format === 'html-js' && <HtmlJsConfigPanel config={htmlJsConfig} onChange={(nextConfig) => setHtmlJsConfig({ ...htmlJsConfig, ...nextConfig })} />}
      {format === 'react' && <ReactConfigPanel config={reactConfig} onChange={(nextConfig) => setReactConfig({ ...reactConfig, ...nextConfig })} />}

      <div className="mt-3 flex gap-2">
        <button onClick={handleExport} disabled={isExporting || !sourceImage} className="btn-primary flex-1">
          {isExporting ? '导出中...' : '开始导出'}
        </button>
        {isExporting && (
          <button onClick={handleCancel} className="btn-secondary">
            取消
          </button>
        )}
      </div>

      {!sourceImage && <div className="xp-note mt-2">请先上传图片并生成预览后再执行导出。</div>}
      {isExporting && <ProgressBar progress={progress} />}
      {result && <ExportResultView result={result} onDownload={handleDownload} onClear={handleClear} />}
    </CollapsibleSection>
  )
}

export default ExportPanel
